import { ApplicationModuleFieldLookup } from "../api/applicationModuleFields";
import {
    backendFormatToDaySchedule,
    convert12HourTo24Hour,
    isValidDayFormat
} from "../../../shared/utils/weeklyScheduleFormatters";
import {
    disabledDaysToSpecialDates,
    formatDateForBackend,
    SpecialDate,
    specialDatesToDisabledDays
} from "../../../shared/utils/scheduleFormatters";

export interface WeeklyHoursAnswerValue {
    mondayHours: string;
    tuesdayHours: string;
    wednesdayHours: string;
    thursdayHours: string;
    fridayHours: string;
    saturdayHours: string;
    sundayHours: string;
}

export interface HolidayOption {
    key: string;
    label: string;
    date: string;
    year: number;
}

export interface ProcedureTypeAnswerItem {
    additionalProcedureTypeIdsToLookUp: null;
    appointmentDuration?: number;
    procedureTypeDescription: string;
    procedureTypeId?: string;
}

export type SpecialDatesAnswerValue = SpecialDate[];

export type MultiChoiceAnswerValue = string[];

export type ProcedureTypeAnswerValue = ProcedureTypeAnswerItem[];

export type OnboardingAnswerValue = string | WeeklyHoursAnswerValue | SpecialDatesAnswerValue | MultiChoiceAnswerValue | ProcedureTypeAnswerValue;

export const dayVariableMap: Array<{ key: keyof WeeklyHoursAnswerValue; variable: string; label: string }> = [
    { key: "mondayHours", variable: "{MONDAY}", label: "Monday" },
    { key: "tuesdayHours", variable: "{TUESDAY}", label: "Tuesday" },
    { key: "wednesdayHours", variable: "{WEDNESDAY}", label: "Wednesday" },
    { key: "thursdayHours", variable: "{THURSDAY}", label: "Thursday" },
    { key: "fridayHours", variable: "{FRIDAY}", label: "Friday" },
    { key: "saturdayHours", variable: "{SATURDAY}", label: "Saturday" },
    { key: "sundayHours", variable: "{SUNDAY}", label: "Sunday" }
];

const fallbackHours: WeeklyHoursAnswerValue = {
    mondayHours: "9:00 AM - 9:00 PM",
    tuesdayHours: "9:00 AM - 9:00 PM",
    wednesdayHours: "9:00 AM - 9:00 PM",
    thursdayHours: "9:00 AM - 9:00 PM",
    fridayHours: "9:00 AM - 10:00 PM",
    saturdayHours: "10:00 AM - 10:00 PM",
    sundayHours: "10:00 AM - 8:00 PM"
};

export const isWeeklyHoursAnswerValue = (value: OnboardingAnswerValue): value is WeeklyHoursAnswerValue => {
    return typeof value === "object" && value !== null && "mondayHours" in value;
};

export const isSpecialDatesAnswerValue = (value: OnboardingAnswerValue | unknown): value is SpecialDatesAnswerValue => {
    return Array.isArray(value) && value.every(entry => typeof entry === "object" && entry !== null && "date" in entry && "isClosedAllDay" in entry);
};

export const isMultiChoiceAnswerValue = (value: OnboardingAnswerValue | unknown): value is MultiChoiceAnswerValue => {
    return Array.isArray(value) && value.every(entry => typeof entry === "string");
};

export const isProcedureTypeAnswerValue = (value: OnboardingAnswerValue | unknown): value is ProcedureTypeAnswerValue => {
    return Array.isArray(value) && value.every(entry => typeof entry === "object" && entry !== null && "procedureTypeDescription" in entry && "additionalProcedureTypeIdsToLookUp" in entry);
};

export const normalizeProcedureTypeAnswerValue = (value: unknown): ProcedureTypeAnswerValue => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map(entry => ({
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: typeof entry?.appointmentDuration === "number" && !Number.isNaN(entry.appointmentDuration)
            ? entry.appointmentDuration
            : (typeof entry?.appointmentDuration === "string" && entry.appointmentDuration.trim() !== "" && !Number.isNaN(parseInt(entry.appointmentDuration, 10))
                ? parseInt(entry.appointmentDuration, 10)
                : undefined),
        procedureTypeDescription: typeof entry?.procedureTypeDescription === "string" ? entry.procedureTypeDescription : "",
        procedureTypeId: typeof entry?.procedureTypeId === "string" ? entry.procedureTypeId : ""
    }));
};

export const formatProcedureTypeAnswerValue = (value: ProcedureTypeAnswerValue): string => {
    return JSON.stringify(normalizeProcedureTypeAnswerValue(value).map(entry => ({
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: entry.appointmentDuration,
        procedureTypeDescription: entry.procedureTypeDescription,
        procedureTypeId: entry.procedureTypeId || ""
    })), null, 2);
};

export const createDefaultWeeklyHoursAnswer = (override?: Partial<WeeklyHoursAnswerValue>): WeeklyHoursAnswerValue => ({
    ...fallbackHours,
    ...override
});

export const getModuleFieldValue = (
    lookup: ApplicationModuleFieldLookup,
    associatedVariable: string,
    fallback = ""
): string => {
    const field = lookup?.[associatedVariable];
    if (field?.currentValue !== undefined && field.currentValue !== null && field.currentValue !== "") {
        return field.currentValue;
    }

    if (field?.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== "") {
        return field.defaultValue;
    }

    return fallback;
};

export const getFirstModuleFieldValue = (
    lookup: ApplicationModuleFieldLookup,
    associatedVariables: string[],
    fallback = ""
): string => {
    for (const associatedVariable of associatedVariables) {
        const value = getModuleFieldValue(lookup, associatedVariable, "");
        if (value !== "") {
            return value;
        }
    }

    return fallback;
};

export const parseBooleanString = (value: string, fallback = ""): string => {
    const normalized = (value || "").trim().toLowerCase();

    if (["true", "yes", "y", "1"].includes(normalized)) {
        return "yes";
    }

    if (["false", "no", "n", "0"].includes(normalized)) {
        return "no";
    }

    return fallback;
};

export const booleanAnswerToString = (value: string): string => value === "yes" ? "true" : "false";

export const inverseBooleanAnswerToString = (value: string): string => value === "yes" ? "false" : "true";

export const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    if (digits.length === 0) {
        return "";
    }

    const hasCountryCode = digits.length > 10 && digits.startsWith("1");
    const workingDigits = hasCountryCode ? digits.slice(1) : digits.slice(0, 10);
    const area = workingDigits.slice(0, 3);
    const prefix = workingDigits.slice(3, 6);
    const line = workingDigits.slice(6, 10);

    let formatted = "";

    if (area.length > 0) {
        formatted = `(${area}`;
    }

    if (area.length === 3) {
        formatted += ")";
    }

    if (prefix.length > 0) {
        formatted += ` ${prefix}`;
    }

    if (line.length > 0) {
        formatted += `-${line}`;
    }

    if (hasCountryCode) {
        return `1 ${formatted}`.trim();
    }

    return formatted.trim();
};

export const getPhoneDigitsForSave = (value: string): string => {
    const digits = value.replace(/\D/g, "");

    if (!digits) {
        return "";
    }

    if (digits.length === 10) {
        return `1${digits}`;
    }

    return digits.slice(0, 11);
};

const normalizeTimeInput = (value: string): string => {
    const trimmed = value.trim().toUpperCase().replace(/\s+/g, " ");
    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*([AP]M)$/);

    if (!match) {
        return trimmed;
    }

    const hour = match[1];
    const minute = match[2] || "00";
    const meridiem = match[3];

    return `${parseInt(hour, 10)}:${minute} ${meridiem}`;
};

const parseDisplayHoursPeriod = (period: string): string | null => {
    const normalized = period.replace(/\s+to\s+/i, " - ").trim();
    const segments = normalized.split(/\s*-\s*/);

    if (segments.length !== 2) {
        return null;
    }

    try {
        return `${convert12HourTo24Hour(normalizeTimeInput(segments[0]))},${convert12HourTo24Hour(normalizeTimeInput(segments[1]))}`;
    } catch (_) {
        return null;
    }
};

export const formatHoursForDisplay = (backendValue: string): string => {
    const trimmed = (backendValue || "").trim();

    if (!trimmed) {
        return "";
    }

    if (trimmed === "00:00,00:01") {
        return "Closed";
    }

    if (trimmed === "00:00,23:59") {
        return "Open 24 hours";
    }

    if (!isValidDayFormat(trimmed)) {
        return trimmed;
    }

    const schedule = backendFormatToDaySchedule("Day", trimmed);
    return schedule.timeRanges.map(range => `${range.startTime} - ${range.endTime}`).join("; ");
};

export const parseHoursForSave = (displayValue: string): string => {
    const trimmed = (displayValue || "").trim();

    if (!trimmed) {
        return "";
    }

    if (isValidDayFormat(trimmed)) {
        return trimmed;
    }

    const normalized = trimmed.toLowerCase();
    if (["closed", "closed all day"].includes(normalized)) {
        return "00:00,00:01";
    }

    if (["open 24 hours", "open all day", "24 hours", "24/7"].includes(normalized)) {
        return "00:00,23:59";
    }

    const periods = trimmed.split(/\s*[;|]\s*/).map(parseDisplayHoursPeriod).filter(Boolean) as string[];
    return periods.length > 0 ? periods.join("|") : trimmed;
};

export const getWeeklyHoursAnswerFromLookup = (lookup: ApplicationModuleFieldLookup): WeeklyHoursAnswerValue => {
    const resolvedHours = dayVariableMap.reduce((accumulator, entry) => {
        accumulator[entry.key] = formatHoursForDisplay(getModuleFieldValue(lookup, entry.variable, fallbackHours[entry.key]));
        return accumulator;
    }, {} as WeeklyHoursAnswerValue);

    return createDefaultWeeklyHoursAnswer(resolvedHours);
};

export const getWeeklyHoursUpdates = (answer: WeeklyHoursAnswerValue) => {
    return dayVariableMap.map(entry => ({
        associatedVariable: entry.variable,
        value: parseHoursForSave(answer[entry.key])
    }));
};

const nthWeekdayOfMonth = (year: number, monthIndex: number, weekday: number, occurrence: number): string => {
    const date = new Date(Date.UTC(year, monthIndex, 1));
    const offset = (weekday - date.getUTCDay() + 7) % 7;
    date.setUTCDate(1 + offset + ((occurrence - 1) * 7));
    return date.toISOString().slice(0, 10);
};

const lastWeekdayOfMonth = (year: number, monthIndex: number, weekday: number): string => {
    const date = new Date(Date.UTC(year, monthIndex + 1, 0));
    const offset = (date.getUTCDay() - weekday + 7) % 7;
    date.setUTCDate(date.getUTCDate() - offset);
    return date.toISOString().slice(0, 10);
};

const buildHolidayOption = (label: string, date: string): HolidayOption => ({
    key: `${label}-${date}`,
    label,
    date,
    year: parseInt(date.slice(0, 4), 10)
});

const formatSpecialDateLine = (specialDate: SpecialDate): string => {
    const backendDate = formatDateForBackend(specialDate.date);
    if (!backendDate) {
        return "";
    }

    if (specialDate.isClosedAllDay || specialDate.timeRanges.length === 0) {
        return backendDate;
    }

    const ranges = specialDate.timeRanges
        .map(timeRange => `${timeRange.startTime} - ${timeRange.endTime}`)
        .join(", ");

    return `${backendDate} (${ranges})`;
};

export const normalizeSpecialDatesAnswer = (value: Partial<SpecialDate>[] | undefined | null): SpecialDatesAnswerValue => {
    if (!Array.isArray(value)) {
        return [];
    }

    const seenDates = new Set<string>();

    return value.reduce((accumulator, entry, index) => {
        const date = typeof entry?.date === "string" ? entry.date.trim() : "";
        if (!date || seenDates.has(date)) {
            return accumulator;
        }

        seenDates.add(date);
        accumulator.push({
            id: typeof entry?.id === "string" && entry.id.trim() ? entry.id : `special-date-${date}-${index}`,
            date,
            isClosedAllDay: entry?.isClosedAllDay !== false,
            timeRanges: Array.isArray(entry?.timeRanges) ? entry.timeRanges.map((timeRange, timeIndex) => ({
                id: typeof timeRange?.id === "string" && timeRange.id.trim() ? timeRange.id : `special-time-${date}-${timeIndex}`,
                startTime: typeof timeRange?.startTime === "string" ? timeRange.startTime : "",
                endTime: typeof timeRange?.endTime === "string" ? timeRange.endTime : ""
            })).filter(timeRange => timeRange.startTime && timeRange.endTime) : []
        });

        return accumulator;
    }, [] as SpecialDatesAnswerValue).sort((left, right) => left.date.localeCompare(right.date));
};

export const getSpecialDatesAnswerFromLookup = (lookup: ApplicationModuleFieldLookup): SpecialDatesAnswerValue => {
    const currentYear = new Date().getFullYear();
    const holidayDateSet = new Set(
        generateCommonUsHolidayOptions(currentYear - 1, currentYear + 5).map(h => h.date)
    );
    return normalizeSpecialDatesAnswer(disabledDaysToSpecialDates(getModuleFieldValue(lookup, "DISABLED_DAYS"), holidayDateSet));
};

export const createHolidaySpecialDate = (date: string): SpecialDate => ({
    id: `holiday-${date}`,
    date,
    isClosedAllDay: true,
    timeRanges: []
});

export const createCustomSpecialDate = (date: string, timeRanges?: SpecialDate["timeRanges"]): SpecialDate => ({
    id: `custom-${date}`,
    date,
    isClosedAllDay: !timeRanges || timeRanges.length === 0,
    timeRanges: timeRanges ?? []
});

export const parseSpecialDatesForSave = (value: SpecialDatesAnswerValue | string): string => {
    if (typeof value === "string") {
        if (!value.trim()) {
            return "";
        }

        return value
            .split(/\n|,/) 
            .map(entry => entry.trim())
            .filter(Boolean)
            .join(",");
    }

    return specialDatesToDisabledDays(normalizeSpecialDatesAnswer(value));
};

export const formatSpecialDatesForDisplay = (value: SpecialDatesAnswerValue | string): string => {
    if (typeof value === "string") {
        if (!value.trim()) {
            return "";
        }

        return value
            .split(",")
            .map(entry => entry.trim())
            .filter(Boolean)
            .join("\n");
    }

    return normalizeSpecialDatesAnswer(value)
        .map(formatSpecialDateLine)
        .filter(Boolean)
        .join("\n");
};

export const generateCommonUsHolidayOptions = (startYear: number, endYear: number): HolidayOption[] => {
    const holidays: HolidayOption[] = [];

    for (let year = startYear; year <= endYear; year += 1) {
        holidays.push(buildHolidayOption("New Year's Day", `${year}-01-01`));
        holidays.push(buildHolidayOption("Martin Luther King Jr. Day", nthWeekdayOfMonth(year, 0, 1, 3)));
        holidays.push(buildHolidayOption("Presidents Day", nthWeekdayOfMonth(year, 1, 1, 3)));
        holidays.push(buildHolidayOption("Memorial Day", lastWeekdayOfMonth(year, 4, 1)));
        holidays.push(buildHolidayOption("Juneteenth", `${year}-06-19`));
        holidays.push(buildHolidayOption("Independence Day", `${year}-07-04`));
        holidays.push(buildHolidayOption("Labor Day", nthWeekdayOfMonth(year, 8, 1, 1)));
        holidays.push(buildHolidayOption("Columbus Day", nthWeekdayOfMonth(year, 9, 1, 2)));
        holidays.push(buildHolidayOption("Veterans Day", `${year}-11-11`));
        holidays.push(buildHolidayOption("Thanksgiving Day", nthWeekdayOfMonth(year, 10, 4, 4)));
        holidays.push(buildHolidayOption("Christmas Eve", `${year}-12-24`));
        holidays.push(buildHolidayOption("Christmas Day", `${year}-12-25`));
    }

    return holidays;
};

export const joinLabeledSections = (sections: Array<{ title: string; value: string }>): string => {
    return sections
        .map(section => ({
            title: section.title,
            value: section.value?.trim() || ""
        }))
        .filter(section => section.value.length > 0)
        .map(section => `${section.title}: ${section.value}`)
        .join("\n\n");
};

export const sanitizeMultilineText = (value: string): string => value?.trim() || "";

export const buildClosedGreeting = (value: string): string => {
    const trimmed = sanitizeMultilineText(value);

    if (!trimmed) {
        return "";
    }

    const suffix = "We're currently closed, but I may still be able to help answer your question.";
    return trimmed.endsWith(suffix) ? trimmed : `${trimmed} ${suffix}`;
};
